import { useState } from 'react'

import { useTranslation } from 'react-i18next'

import type { HTMLChakraProps } from '@enechain/ecloud-designsystem'
import {
  Box,
  HStack,
  MenuRoot,
  PrimaryButton,
  MenuContent as RawMenuContent,
  MenuItem as RawMenuItem,
  MenuPositioner as RawMenuPositioner,
  MenuTrigger as RawMenuTrigger,
  TertiaryButton,
  Text,
  Tooltip,
} from '@enechain/ecloud-designsystem'
import {
  FaArrowDownToBracketRegular,
  FaChevronDownRegular,
  FaCloudArrowUpRegular,
  FaPlusRegular,
} from '@enechain/ecloud-icons'

import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import { iconSize } from '~/constants/iconSize'

/**
 * chakra v3 の `MenuTriggerProps` / `MenuContentProps` / `MenuItemProps` は本プロジェクトの
 * 型解決 (ecloud 0.14 / chakra 3.29 の d.ts 解決問題) で `HTMLChakraProps` が collapse し
 * `children` / `value` / `onSelect` / style props が欠落する。 v3 本来の prop シグネチャ
 * (`HTMLChakraProps<element>` + ark-ui 固有 prop) へ cast して復元する
 * (= SpotBidding/BlockBidTable と同型の cast パターン)。
 */
const MenuTrigger = RawMenuTrigger as React.FC<
  HTMLChakraProps<'button'> & { asChild?: boolean }
>
const MenuContent = RawMenuContent as React.FC<HTMLChakraProps<'div'>>
const MenuPositioner = RawMenuPositioner as React.FC<
  HTMLChakraProps<'div'> & { children?: React.ReactNode }
>
const MenuItem = RawMenuItem as React.FC<
  HTMLChakraProps<'div'> & {
    value: string
    closeOnSelect?: boolean
    onSelect?: () => void
  }
>

type Props = {
  /** 主ボタン (=個別追加の即実行) のハンドラ。 必須。 */
  onAddIndividual: () => void
  /**
   * ▾ dropdown 「CSVアップロード」選択時のハンドラ。 未指定 (= 既定) だと disabled
   * 表示になる (CSV アップロード機能は各画面共通で別 PR スコープのため未実装)。
   * prop 名は API 呼び出し側 (7 タブ) の互換性維持のため `onCsvImport` のまま。
   */
  onCsvImport?: () => void
  /**
   * 「CSVダウンロード」クリック時のハンドラ。 未指定 (= 既定) だと disabled 表示に
   * なる (CSV エクスポート機能は各画面共通で別 PR スコープのため未実装)。
   */
  onCsvExport?: () => void
  /**
   * `onCsvExport` が未指定の時の押せない理由 (一覧の読み込み中・読み込み失敗など)。 ツールチップの文言を
   * 差し替えるだけで、 ボタンの横に文は出さない。 省略時は未実装として「準備中」 を出す。
   */
  csvExportDisabledReason?: string
  /**
   * 主ボタンのラベル。 省略時は `common:masterTableActionBar.create` (「追加」)。
   * 呼び出し画面固有の文言 (例: 「特異日を追加」) を出す時に指定する。
   */
  createLabel?: string
  /**
   * ▾ dropdown 「CSVアップロード」項目の 2 行目 (description) 文言。
   * 省略時は `common:masterTableActionBar.csvUploadDesc` (形式非依存の汎用文言)。
   * CSV 形式が画面固有 (例: OCCTO マスタ形式) の場合に画面側で指定する。
   */
  csvUploadDescLabel?: string
  /**
   * CSVダウンロード ボタンの位置 (追加 split-button に対する相対位置)。
   *
   * - `'before'` (既定): CSVダウンロード → 追加 split の順 (design `admin/occto.html` §「テーブル
   *   右上のアクションバー」準拠、 OCCTO マスタ 8 タブと同型)
   * - `'after'`: 追加 split → CSVダウンロード の順 (design `demand/special-day-calendar.html`
   *   `#sdc-add-slot` の `addMenuHtml() + csvDownloadLinkHtml()` 準拠、 特異日カレンダー)
   *
   * design SoT が画面によって順序を分けているため画面側で明示する。
   */
  csvExportPosition?: 'before' | 'after'
  /**
   * true の時、 コンポーネント外側の spacing (marginTop / paddingBottom) を 0 にする。
   *
   * 既定 = false は独立行として並べられる場合 (OCCTO マスタ: FilterToolbar と Table の間)
   * を想定した spacing。 true は親側でレイアウトを持つ page-head 等に埋め込む場合
   * (特異日カレンダー: `.sdc-page-head` の右端 `align-items: flex-start`) に使う。
   * 埋め込み時に既定の marginTop=4 が居ると description との top 揃えが崩れる。
   */
  embed?: boolean
  /**
   * 「CSVアップロード」導線 (▾ split button) を表示するか。 既定 = true。
   *
   * `false` の時は ▾ / dropdown を描画せず「追加」を plain PrimaryButton に縮退させる。
   * 広域機関の全 8 タブが `AdminOcctoMaster/shell/MasterCrudTabShell` 経由で `false` を渡す:
   * #5474 の P0 判断で画面内 CSV インポートを撤去し (`uploads/common.md` 決定表)、 取引先タブに
   * 残していた準備中の ▾ (旧 AC-MOI-020) も spec で欠番になったため (index.md AC-MOI-010〜011・020)。
   */
  showCsvUpload?: boolean
  /**
   * true の時、 主「追加」ボタンを disabled にする。
   * 既定 false (= 他タブへの影響なし)。
   *
   * BG タブ (AC-OMB-025 / AC-MOI-005: 事業者マスタが 1 件も登録されておらず
   * BG区分の選択肢が全て絞り込まれ 0 件になる状態) が唯一の利用箇所。
   */
  addDisabled?: boolean
  /**
   * `addDisabled=true` の時にホバー・フォーカスで表示する説明文言 (ツールチップだけ)。
   * 未指定の場合は Tooltip を出さない (`ButtonTooltip` の仕様どおり)。
   *
   * ボタンの隣に文は出さない (UI 辞書 action-reason: 押せない理由をボタンの隣に常に出す文は
   * 2026-10-14 に廃止)。 業務が止まる理由 (設定の不足) を画面上に出す時は、 呼び出し側が
   * 画面上部の info Alert で出す (広域機関 BG タブ = `MasterCrudTabShell`)。
   */
  addDisabledReason?: string
}

/**
 * マスタ画面共通の「テーブル右上アクションバー」。
 *
 * 元は OCCTO マスタ 8 タブ (`AdminOcctoMaster/tabs/_common/components/`) 専用
 * だったが、 特異日カレンダー (`AdminSingularDay`) 等でも同じ split-button 形状が
 * 必要になったため `~/components/common/` へ昇格して共通化した (2026-08-05)。
 *
 * design (`design/admin/occto.html` `.occ-add-menu-list` /
 * `design/demand/special-day-calendar.html` `#sdc-add-slot`) 準拠: 「CSVダウンロード」
 * (tertiary) + 「追加」split button (primary、 主 = 個別入力を 1 クリックで即実行、
 * ▾ の代替 dropdown で 「CSVアップロード」 1 項目のみ)。 design/CLAUDE.md §2 の
 * split button 規約: 主ボタン (個別入力) は再掲せず ▾ には**代替の方法だけ**を並べる。
 * ▾ 内 item は **全項目 2 行必須** (`.ecl-menu-item--rich` = title 600 + desc xs
 * mid-emphasis)。
 *
 * ecloud custom `Menu` は content 型が `label: string` 固定で 2 行 item を表現できないため、
 * split ▾ ではここだけ chakra 素の `MenuRoot`/`MenuTrigger`/`MenuPositioner`/`MenuContent`/
 * `MenuItem` に落とし、 recipe defaults (`alignItems: center` / `py: 1.5`) を明示的に
 * override して title + desc の 2 行を組む。 `MenuPositioner` を挟むのは、 BG タブで
 * 過去に踏んだ 「positioner 無しで組むと popper 配置が効かず trigger の隣にインライン
 * 描画される」 不具合 (2026-07-10 ユーザー報告) の再発防止。 raw slot 使用の範囲は本
 * split ▾ に封じ込め、 他箇所の dropdown は引き続き ecloud custom `Menu` を使う。
 *
 * ラベルは既定で `common:masterTableActionBar.*` を引く。 画面固有の文言
 * (「特異日を追加」等) は `createLabel` prop で override する。
 *
 * `showCsvUpload={false}` (広域機関の全 8 タブ、 shell 経由) の時は ▾ を描画せず「追加」を
 * plain PrimaryButton に縮退させる。 [design差異] design `occto.html` は 7 タブで CSVアップロード ▾ を
 * 保持するが、 #5474 の P0 判断で画面内 CSV インポートを撤去する決定 (`uploads/common.md`
 * 決定表、 2026-08-18) のため occto では ▾ を出さない (取引先タブは design も ▾ を畳んでいる)。
 * 撤去は共有部品ではなく occto の単一呼び出し点 (`MasterCrudTabShell`) に閉じ込め、
 * 撤去対象外の特異日カレンダーには波及させない。
 */
export const MasterTableActionBar = ({
  onAddIndividual,
  onCsvImport,
  onCsvExport,
  csvExportDisabledReason,
  createLabel,
  csvUploadDescLabel,
  csvExportPosition = 'before',
  embed = false,
  showCsvUpload = true,
  addDisabled = false,
  addDisabledReason,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  // ▾ メニューで強調中の項目。 押せない項目の理由のツールチップを、 ホバーと矢印キーの
  // どちらで強調した時も出すために使う。
  const [highlightedMenuValue, setHighlightedMenuValue] = useState<
    string | null
  >(null)

  const resolvedCreateLabel =
    createLabel ?? t('common:masterTableActionBar.create')
  const resolvedCsvUploadDescLabel =
    csvUploadDescLabel ?? t('common:masterTableActionBar.csvUploadDesc')

  // 押せない理由はツールチップだけ。 読み込み中・読み込み失敗は表の領域のローディング・エラー表示が
  // 画面に出ているので文を重ねない (UI 辞書 action-reason の常時の文は 2026-10-14 に廃止)。
  const csvNotImplementedReason = t(
    'common:masterTableActionBar.csvNotImplemented',
  )
  const csvExportUnavailableReason =
    onCsvExport === undefined
      ? (csvExportDisabledReason ?? csvNotImplementedReason)
      : undefined
  const csvUploadUnavailableReason =
    onCsvImport === undefined ? csvNotImplementedReason : undefined
  const csvExportButton = (
    <ButtonTooltip content={csvExportUnavailableReason}>
      <TertiaryButton
        disabled={onCsvExport === undefined}
        onClick={onCsvExport}
        title={csvExportUnavailableReason}
      >
        <FaArrowDownToBracketRegular boxSize={iconSize.labeledButton} />
        {t('common:masterTableActionBar.csvExport')}
      </TertiaryButton>
    </ButtonTooltip>
  )

  // showCsvUpload={false} (広域機関の全 8 タブ、 #5474) の時は ▾ の唯一の項目
  // (CSVアップロード) を撤去するため、 ▾ 自体を描画せず「追加」を plain PrimaryButton
  // に縮退させる。 撤去は occto の呼び出し点に閉じ込め、 撤去対象外の特異日カレンダー
  // (既定 showCsvUpload=true) には波及させない。
  // 押せない理由 (AC-OMB-025): 非活性の条件 (addDisabled) とツールチップを同じ値から導く。
  // ボタンの隣に文は出さない (UI 辞書 action-reason、 2026-10-14 廃止)。 画面上の文は
  // 呼び出し側の画面上部 info Alert が持つ (広域機関 = MasterCrudTabShell)。
  const addDisabledReasonText = addDisabled ? addDisabledReason : undefined

  const addSplitButton = !showCsvUpload ? (
    // AC-OMB-025 / AC-MOI-005 (BG タブ例外): addDisabled=true の時は「新規登録」を
    // disabled にし、 ButtonTooltip (CSVダウンロードボタンと同じパターン) で
    // 押せない理由をホバー表示する。 addDisabledReason 未指定なら Tooltip は
    // 出ない (= 他タブは既定 addDisabled=false のため無変化)。
    <ButtonTooltip content={addDisabledReasonText}>
      <PrimaryButton
        disabled={addDisabled}
        leftIcon={<FaPlusRegular />}
        onClick={onAddIndividual}
      >
        {resolvedCreateLabel}
      </PrimaryButton>
    </ButtonTooltip>
  ) : (
    // split button (design/CLAUDE.md §2「split button」): 主ボタン (個別入力) は
    // 1 クリックで即実行し、 隣接の ▾ だけがメニュー (CSVアップロード等の代替) を
    // 開く。 HStack gap={0} + 主/▾ の隣接コーナー borderRadius=0 で 1 ボタン
    // 外観に見せ、 subtle な区切り線を左端 border で表現する。
    // 区切り線の色は文字色の 25% (`color-mix`) で、 `marginLeft="-1px"` で主ボタンの右 border に重ねる
    // (design/ui-feasibility/components/split-button.md「dev 実装の現状」。 計画作成 = pages/ProcurementPlanList と
    // 同じ書き方)。 以前は `Border.onColorLowEmphasis` 固定で、 variant を変えると線が追随しなかった。
    <HStack gap={0}>
      <ButtonTooltip content={addDisabledReasonText}>
        <PrimaryButton
          borderTopRightRadius={0}
          borderBottomRightRadius={0}
          disabled={addDisabled}
          leftIcon={<FaPlusRegular />}
          onClick={onAddIndividual}
        >
          {resolvedCreateLabel}
        </PrimaryButton>
      </ButtonTooltip>
      <MenuRoot
        // MenuRoot の props も型解決で collapse するため、 引数の型を明示する
        // (上の MenuTrigger 等の cast と同じ理由)。
        onHighlightChange={(details: { highlightedValue: string | null }) => {
          setHighlightedMenuValue(details.highlightedValue)
        }}
      >
        <MenuTrigger asChild>
          <PrimaryButton
            marginLeft="-1px"
            borderLeftWidth="1px"
            // DS PrimaryButton の recipe は `border: none` (border-style: none) なので、 style を
            // 足さないと幅と色を指定しても線が出ない (以前の Border.onColorLowEmphasis 指定は
            // computed で 0px になっており、 区切り線が一度も描かれていなかった)。
            borderLeftStyle="solid"
            borderLeftColor="color-mix(in srgb, currentColor 25%, transparent)"
            borderTopLeftRadius={0}
            borderBottomLeftRadius={0}
            aria-label={t('common:masterTableActionBar.createMenuAriaLabel')}
            paddingX={2}
          >
            {/* [DS差異] design/CLAUDE.md「アイコン variant」表では caret ▾ は Solid 固定だが、
                @enechain/ecloud-icons には FaChevronDownSolid が存在しないため Regular を採用。 */}
            <FaChevronDownRegular />
          </PrimaryButton>
        </MenuTrigger>
        <MenuPositioner>
          {/* design css `.ecl-menu:has(.ecl-menu-item--rich) { min-width: 264px }`
              準拠。 desc が折返しても最低幅を確保する。 */}
          <MenuContent minWidth="264px">
            {/* design HTML `.occ-add-menu-list` / `#sdc-add-slot` の add-menu は
                「CSVアップロード」 1 項目のみ。 主ボタン (個別入力) を ▾ に再掲しない
                (= design/CLAUDE.md §2「▾ の項目は主ボタンと結果が同じで方法だけが
                違う **代替**」)。 */}
            <RichMenuItem
              description={resolvedCsvUploadDescLabel}
              disabled={onCsvImport === undefined}
              disabledReason={csvUploadUnavailableReason}
              highlighted={highlightedMenuValue === 'csv-upload'}
              icon={<FaCloudArrowUpRegular />}
              onSelect={onCsvImport}
              title={t('common:masterTableActionBar.csvUpload')}
              value="csv-upload"
            />
          </MenuContent>
        </MenuPositioner>
      </MenuRoot>
    </HStack>
  )

  return (
    // flexShrink=0 は design `#sdc-add-slot { flex-shrink: 0 }` (special-day-calendar.html:46)
    // 準拠。 親が横並び Flex (= 特異日カレンダーの .sdc-page-head 相当) の時に
    // 描画スペースが狭くなってもボタン列が縮まず、 かつ折り返して左寄せに退化するのを防ぐ。
    // OCCTO タブ側は親が縦積み Flex なので shrink 契機が発生せず影響なし。
    <HStack
      justify="flex-end"
      flexShrink={0}
      gap={2}
      marginTop={embed ? 0 : 4}
      paddingBottom={embed ? 0 : 2}
    >
      {csvExportPosition === 'before' ? (
        <>
          {csvExportButton}
          {addSplitButton}
        </>
      ) : (
        <>
          {addSplitButton}
          {csvExportButton}
        </>
      )}
    </HStack>
  )
}

type RichMenuItemProps = {
  icon: React.ReactElement
  title: string
  description: string
  value: string
  disabled?: boolean
  /** 押せない理由。 指定時だけ項目自体を Tooltip の trigger にして、 強調中に出す。 */
  disabledReason?: string
  /** メニューで強調中か (ホバー・矢印キーのどちらでも true になる)。 */
  highlighted: boolean
  onSelect?: () => void
}

/**
 * design `.ecl-menu-item--rich` 準拠の 2 行 item (design/CLAUDE.md §2 split ▾)。
 * chakra `MenuItem` recipe の base defaults (`alignItems: center` / `py: 1.5`) を
 * override し、 icon の縦位置は title と揃える (`marginTop: 2px`)。
 *
 * 押せない項目は `disabled` にせず `aria-disabled` で示す。 ark-ui (zag) の menu は
 * `disabled` の項目を矢印キーで飛ばすため、 キーボードでは理由のツールチップに届かない
 * (UI 辞書 action-reason 区分 2・AC-MSD-017。 Des は prototype#654 でフォーカスでも
 * 理由を出すと決めた)。 選んでも onSelect を呼ばず、 メニューも閉じない。 見た目は
 * chakra の `_disabled` (`[aria-disabled=true]` を含む) がそのまま当たる。
 */
const RichMenuItem = ({
  icon,
  title,
  description,
  value,
  disabled,
  disabledReason,
  highlighted,
  onSelect,
}: RichMenuItemProps): React.ReactElement => (
  <MenuItem
    whiteSpace="normal"
    aria-disabled={disabled === true ? true : undefined}
    closeOnSelect={disabled !== true}
    onSelect={disabled === true ? undefined : onSelect}
    paddingY={2}
    value={value}
  >
    {/* Tooltip は項目の内側に置く。 項目を包むと Tooltip の trigger の id・data-ownedby が
        項目に上書きされ、 menu が矢印キーで項目を見つけられなくなる。 */}
    <Tooltip
      content={disabledReason ?? ''}
      isHidden={disabledReason === undefined || disabledReason === ''}
      open={highlighted}
    >
      <Box alignItems="flex-start" gap={2} display="flex" width="full">
        <Box flexShrink={0} display="flex" marginTop="2px">
          {icon}
        </Box>
        <Box
          alignItems="flex-start"
          flexDirection="column"
          gap={0.5}
          display="flex"
        >
          <Text fontWeight="semibold">{title}</Text>
          <Text
            textStyle="menuItemDesc"
            color="Text.midEmphasis"
            whiteSpace="normal"
          >
            {description}
          </Text>
        </Box>
      </Box>
    </Tooltip>
  </MenuItem>
)
